import * as Yup from 'yup';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Alert,
  Box,
  Typography,
  TextField,
  Button,
  FormHelperText,
  Checkbox,
  FormControl,
  FormControlLabel,
  Link,
  Divider,
} from '@mui/material';
import useI18nForm from '../../components/common/hooks/useI18nForm';
import useAuth0 from 'common/hooks/useAuth0';
import { GetFamiliplusProfile, GiveToOES } from '../../services/profile/ProfileService';
import FamiliplusProfile from './FamiliplusProfile';
import { useNavigate } from 'common/hooks/useNavigate';
import LoadingIndicator from 'components/common/LoadingIndicator';
import i18n from 'i18n';
import useApi, { Status } from 'common/hooks/useApi';
import { Helmet } from 'react-helmet';
import { SyncAccount } from 'components/common/syncAccount';

function getPointsValue(points: number) {
  return (Math.round((points / 100) * 100) / 100).toFixed(2);
}

const FamiliplusOESDonations = (): JSX.Element => {
  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetFamiliplusProfile);
  const { t } = useTranslation('component');
  const navigate = useNavigate();
  const { user } = useAuth0();

  const isDataLoading = fetchingProfileStatus === Status.Pending;
  const hasFetchDataFailed = fetchingProfileStatus === Status.Rejected;
  const isDataResolved = fetchingProfileStatus === Status.Resolved;

  useEffect(() => {
    if (user?.session_id) {
      fetchProfile(user!.session_id);
    }
  }, [fetchProfile, user]);

  useEffect(() => {
    if (
      profile &&
      (profile.ProfileCompletedStatus == 'NotFamiliplus' || (profile.Card?.CardNumber || '').length == 0)
    ) {
      navigate(`/${i18n.resolvedLanguage}/${i18n.resolvedLanguage == 'fr' ? 'Compte' : 'Account'}/Familiplus`);
    }
  }, [navigate, profile]);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.OESTitle')}</title>
        <meta name="description" content={t('MetaTag.OESDescription').toString()} />
      </Helmet>

      {isDataLoading && <LoadingIndicator />}

      {hasFetchDataFailed && (
        <Alert id="fetchAlert" severity="error" sx={{ mb: '16px' }}>
          {t('Errors.GenericError')}
        </Alert>
      )}

      {isDataResolved && profile?.Card && <FamiliplusOESDonationForm profile={profile} />}
    </>
  );
};

const FamiliplusOESDonationForm = (props: { profile: FamiliplusProfile }): JSX.Element => {
  const { user, getAccessTokenSilently } = useAuth0();
  const [errorMessages, setErrorMessages] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pointsGiven, setpointsGiven] = useState<number>(0);
  const { t } = useTranslation(['familiplusPage', 'component']);

  const maxPoints = props.profile.Card!.Points;

  const initialValues = {
    pointsGiven: 0,
    consentToDonate: false,
  };

  const validationSchema = Yup.object().shape({
    pointsGiven: Yup.number().max(maxPoints, t('OES.maxPoints').toString()).min(0, t('OES.minPoints').toString()),
    consentToDonate: Yup.boolean().isTrue(t('OES.consent').toString()),
  });

  const handlePointsDonation = async function (values: any) {
    if (!isLoading) {
      setIsLoading(true);
      setSaveMessage('');
      setErrorMessages('');

      try {
        const access_token = await getAccessTokenSilently();
        const profileAuth = { sessionId: user?.session_id, token: access_token };

        if (values.pointsGiven > 0 && values.pointsGiven <= props.profile.Card!.Points && values.consentToDonate) {
          const donationResponse = await GiveToOES(profileAuth, values.pointsGiven);

          if (donationResponse.ok) {
            setpointsGiven(values.pointsGiven);
            setSaveMessage('OES.donationSuccess');
            props.profile!.Card!.Points -= values.pointsGiven;
            SyncAccount();
          } else {
            setErrorMessages('Errors.GenericError');
          }
        }
      } catch (e) {
        setErrorMessages('Errors.GenericError');
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values: any) => {
      handlePointsDonation(values);
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <Typography sx={{ display: 'block', padding: '25px 0' }} variant="title">
        {t('OES.greeting')}
      </Typography>

      {errorMessages && (
        <Alert id="pageErrorMessage" severity="error" sx={{ mb: '25px' }}>
          {t(errorMessages, { ns: 'component' })}
        </Alert>
      )}
      {saveMessage && (
        <Alert id="pageSuccessMessage" severity="success" sx={{ mb: '25px' }}>
          {t(saveMessage, {
            points: pointsGiven,
            value: getPointsValue(pointsGiven),
          })}
        </Alert>
      )}

      <DonationsPointCard maximumPoints={maxPoints} formik={formik} />

      <DonationConsent formik={formik} />
      <Divider />
      <Grid item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row-reverse',
            alignItems: 'center',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isLoading && <LoadingIndicator />}
            <Button
              disabled={
                !formik.values.pointsGiven ||
                formik.values.pointsGiven <= 0 ||
                !formik.values.consentToDonate ||
                isLoading ||
                formik.values.pointsGiven > maxPoints ||
                formik.values.pointsGiven % 1 != 0
              }
              type="submit"
              size="large"
              variant="contained"
              sx={{ marginLeft: '2em' }}>
              {t('OES.donate', { points: formik.values.pointsGiven })}
            </Button>
          </Box>
        </Box>
      </Grid>
    </form>
  );
};

const DonationConsent = (props: { formik: any }): JSX.Element => {
  const { t } = useTranslation('familiplusPage');

  return (
    <Grid item xs={12} margin={'1em 0'}>
      <FormControl error={props.formik.submitCount > 0 && Boolean(props.formik.errors.consentToDonate)}>
        <FormControlLabel
          sx={{ display: 'flex', alignItems: 'flex-start' }}
          control={<Checkbox name="consentToDonate" />}
          id="consentToDonateCheckbox"
          label={
            <Typography color={'#002C53'} fontSize={'14px'} sx={{ position: 'relative', top: '7px' }}>
              {t('OES.consentLabel')} {t('OES.taxLabel')}{' '}
              {i18n.resolvedLanguage == 'fr' ? (
                <Link
                  style={{ color: 'black' }}
                  target="_blank"
                  href="https://www.familiprix.com/fr/pages/termes-et-conditions-familiplus">
                  {t('OES.legalTerms')}
                </Link>
              ) : (
                <Link
                  style={{ color: 'black' }}
                  target="_blank"
                  href="https://www.familiprix.com/en/pages/terms-and-conditions-familiplus">
                  {t('OES.legalTerms')}
                </Link>
              )}
            </Typography>
          }
          onChange={props.formik.handleChange}
        />
        {props.formik.submitCount > 0 && Boolean(props.formik.errors.consentToDonate) && (
          <FormHelperText sx={{ paddingTop: '10px' }} id="consentToDonateCheckboxErrorText">
            {props.formik.submitCount > 0 ? props.formik.errors.consentToDonate : ''}
          </FormHelperText>
        )}
      </FormControl>
    </Grid>
  );
};

const DonationsPointCard = (props: { maximumPoints: number; formik: any }): JSX.Element => {
  const { t } = useTranslation('familiplusPage');
  const pointsGivenValue = getPointsValue(props.formik.values.pointsGiven);

  return (
    <>
      <Typography mb={1} fontWeight={'bold'}>
        {t('OES.howManyPoints')}
      </Typography>

      <Grid>
        <TextField
          type="number"
          fullWidth
          id="OESDonationPointsInput"
          name="pointsGiven"
          inputProps={{
            min: 0,
            max: props.maximumPoints,
          }}
          placeholder="0"
          onChange={props.formik.handleChange}
          error={Boolean(props.formik.errors.pointsGiven)}
        />
        <Grid
          container
          direction={{ xs: 'column', sm: 'row' }}
          style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography className="pointsValueOES" color={'#4B5A67'}>
            {t('OES.pointsValue', { value: pointsGivenValue })}
          </Typography>

          <Typography className="availablePointsOES" fontSize={'13px'} color={'#99ABBA'}>
            {t('OES.availablePoints', { points: props.maximumPoints, value: getPointsValue(props.maximumPoints) })}
          </Typography>
        </Grid>

        {Boolean(props.formik.errors.pointsGiven) && (
          <FormHelperText id="PointsGivenInputErrorMessage">{props.formik.errors.pointsGiven}</FormHelperText>
        )}
      </Grid>
    </>
  );
};

export default FamiliplusOESDonations;
